import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { TESTIDS } from "@/constants/testIds";
import { Plus, X, Trash2, Save, Search, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

const TYPES = ["buyer", "seller", "bank", "shipowner", "charterer", "agent", "broker", "inspector", "other"];
const RISK_KEYS = [
  { v: "", i18n: "ctp_risk_std" },
  { v: "watch", i18n: "ctp_risk_watch" },
  { v: "blocked", i18n: "ctp_risk_blocked" },
];

const EMPTY_CP = {
  legal_name: "", counterparty_type: "buyer", country: "", registered_address: "",
  vat_number: "", registration_number: "", website: "",
  contacts: [], bank_details: "", risk_flag: "", notes: "",
};

export default function CounterpartiesPage() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // full cp object or new
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setItems(await api.listCounterparties()); }
    catch { toast.error(t("ctp_load_failed")); }
    finally { setLoading(false); }
  }, [t]);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!q) return items;
    const s = q.toLowerCase();
    return items.filter((c) =>
      c.legal_name.toLowerCase().includes(s) ||
      (c.country || "").toLowerCase().includes(s) ||
      c.counterparty_type.toLowerCase().includes(s));
  }, [items, q]);

  const startNew = () => setEditing({ ...EMPTY_CP, __new: true });
  const startEdit = (cp) => setEditing({ ...EMPTY_CP, ...cp, contacts: cp.contacts || [] });
  const cancel = () => setEditing(null);

  const del = async (cp) => {
    if (!window.confirm(t("ctp_confirm_delete"))) return;
    try {
      await api.deleteCounterparty(cp.id);
      setItems(items.filter((x) => x.id !== cp.id));
      toast.success(t("ctp_deleted"));
    } catch { toast.error(t("ctp_delete_failed")); }
  };

  const save = async () => {
    if (!editing.legal_name.trim()) { toast.error(t("ctp_legal_required")); return; }
    setSaving(true);
    try {
      let saved;
      if (editing.__new) {
        const { __new, id, owner_id, created_at, updated_at, ...payload } = editing;
        saved = await api.createCounterparty(payload);
        setItems([saved, ...items]);
      } else {
        const { id, owner_id, created_at, updated_at, ...payload } = editing;
        saved = await api.updateCounterparty(editing.id, payload);
        setItems(items.map((x) => x.id === saved.id ? saved : x));
      }
      toast.success(t("ctp_saved"));
      setEditing(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("cp_save_failed"));
    } finally { setSaving(false); }
  };

  const upd = (k, v) => setEditing((s) => ({ ...s, [k]: v }));
  const addContact = () => setEditing((s) => ({ ...s, contacts: [...s.contacts, { name: "", role: "", email: "", phone: "" }] }));
  const updContact = (i, f, v) => setEditing((s) => ({ ...s, contacts: s.contacts.map((c, idx) => idx === i ? { ...c, [f]: v } : c) }));
  const rmContact = (i) => setEditing((s) => ({ ...s, contacts: s.contacts.filter((_, idx) => idx !== i) }));

  return (
    <div className="px-4 py-6 sm:p-6 md:p-10 xl:p-12 max-w-6xl">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-5 mb-8">
        <div>
          <div className="kicker mb-3">— {t("d_nav_counterparties")}</div>
          <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl mb-2">{t("d_nav_counterparties")}</h1>
          <p className="text-[var(--td-text-2)] max-w-2xl">{t("ctp_intro")}</p>
        </div>
        <button onClick={startNew} data-testid={TESTIDS.ctpNewBtn} className="btn-gold inline-flex items-center justify-center gap-2 shrink-0 w-full sm:w-auto">
          <Plus size={14} /> {t("ctp_new_btn")}
        </button>
      </div>

      <div className="mb-6 flex items-center gap-3 max-w-md">
        <Search size={14} className="text-[var(--td-text-3)]" />
        <input data-testid={TESTIDS.ctpSearch} className="td-input !py-2 flex-1" placeholder={t("ctp_search_ph")}
               value={q} onChange={(e) => setQ(e.target.value)} />
      </div>

      {loading ? (
        <div className="text-[var(--td-text-3)] font-mono text-xs">{t("cp_loading")}</div>
      ) : filtered.length === 0 ? (
        <div className="card-surface p-16 text-center text-[var(--td-text-2)] italic">
          {items.length === 0 ? t("ctp_empty") : t("ctp_no_matches")}
        </div>
      ) : (
        <div className="card-surface divide-y" style={{ borderColor: "var(--td-border)" }}>
          {filtered.map((cp) => (
            <div key={cp.id} data-testid={TESTIDS.ctpRow} className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 hover:bg-[rgba(212,175,55,0.04)]">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                  <span className="font-serif text-lg break-words min-w-0">{cp.legal_name}</span>
                  <span className="font-mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 border border-[var(--td-border)]">{cp.counterparty_type}</span>
                  {cp.risk_flag === "watch" && (
                    <span className="font-mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 border border-amber-500/40 text-amber-400 inline-flex items-center gap-1">
                      <ShieldAlert size={10} /> {t("ctp_risk_watch")}
                    </span>
                  )}
                  {cp.risk_flag === "blocked" && (
                    <span className="font-mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 border border-red-500/60 text-red-400 inline-flex items-center gap-1">
                      <ShieldAlert size={10} /> {t("ctp_risk_blocked")}
                    </span>
                  )}
                </div>
                <div className="text-xs text-[var(--td-text-3)] font-mono mt-1 truncate">
                  {[cp.country, cp.vat_number].filter(Boolean).join(" · ") || "—"}
                </div>
              </div>
              <div className="flex items-center justify-end gap-2 shrink-0 w-full sm:w-auto">
                <button onClick={() => startEdit(cp)} data-testid={TESTIDS.ctpEdit}
                        className="font-mono text-[10px] uppercase tracking-widest text-[var(--td-gold)] hover:text-[var(--td-gold-hover)] min-h-11 px-3">
                  {t("ctp_edit_btn")}
                </button>
                <button onClick={() => del(cp)} data-testid={TESTIDS.ctpDelete}
                        className="text-[var(--td-text-3)] hover:text-red-400 min-h-11 min-w-11 inline-flex items-center justify-center">
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-start justify-center p-4 md:p-6 overflow-y-auto">
          <div className="card-surface w-full max-w-3xl p-8 my-8 relative">
            <button onClick={cancel} data-testid={TESTIDS.ctpModalClose} className="absolute top-4 right-4 text-[var(--td-text-3)] hover:text-[var(--td-text)]">
              <X size={18} />
            </button>
            <div className="kicker mb-3">— {editing.__new ? t("ctp_new_kicker") : t("ctp_edit_kicker")}</div>
            <h2 className="font-serif text-3xl mb-8">{editing.legal_name || t("ctp_untitled")}</h2>

            <div className="grid md:grid-cols-2 gap-5 mb-5">
              <div>
                <label className="td-label">{t("ctp_f_legal_name")}</label>
                <input data-testid={TESTIDS.ctpLegalName} className="td-input" value={editing.legal_name} onChange={(e) => upd("legal_name", e.target.value)} />
              </div>
              <div>
                <label className="td-label">{t("ctp_f_type")}</label>
                <select data-testid={TESTIDS.ctpType} className="td-input" value={editing.counterparty_type} onChange={(e) => upd("counterparty_type", e.target.value)}>
                  {TYPES.map((ty) => <option key={ty} value={ty}>{ty}</option>)}
                </select>
              </div>
              <div>
                <label className="td-label">{t("ctp_f_country")}</label>
                <input data-testid={TESTIDS.ctpCountry} className="td-input" value={editing.country || ""} onChange={(e) => upd("country", e.target.value)} />
              </div>
              <div>
                <label className="td-label">{t("ctp_f_vat")}</label>
                <input data-testid={TESTIDS.ctpVat} className="td-input" value={editing.vat_number || ""} onChange={(e) => upd("vat_number", e.target.value)} />
              </div>
              <div className="md:col-span-2">
                <label className="td-label">{t("ctp_f_address")}</label>
                <textarea data-testid={TESTIDS.ctpAddress} rows={2} className="td-input resize-none"
                          value={editing.registered_address || ""} onChange={(e) => upd("registered_address", e.target.value)} />
              </div>
              <div>
                <label className="td-label">{t("ctp_f_risk")}</label>
                <select data-testid={TESTIDS.ctpRisk} className="td-input" value={editing.risk_flag || ""} onChange={(e) => upd("risk_flag", e.target.value)}>
                  {RISK_KEYS.map((r) => <option key={r.v} value={r.v}>{t(r.i18n)}</option>)}
                </select>
              </div>
              <div>
                <label className="td-label">{t("ctp_f_website")}</label>
                <input data-testid={TESTIDS.ctpWebsite} className="td-input" value={editing.website || ""} onChange={(e) => upd("website", e.target.value)} />
              </div>
            </div>

            <div className="mb-5">
              <div className="flex items-center justify-between mb-2">
                <label className="td-label !mb-0">{t("ctp_f_contacts")}</label>
                <button onClick={addContact} data-testid={TESTIDS.ctpContactAdd}
                        className="font-mono text-[10px] uppercase tracking-widest text-[var(--td-gold)] hover:text-[var(--td-gold-hover)] inline-flex items-center gap-1.5">
                  <Plus size={11} /> {t("ctp_add_contact")}
                </button>
              </div>
              <div className="space-y-2">
                {(editing.contacts || []).map((c, i) => (
                  <div key={i} className="grid grid-cols-[1.2fr_1fr_1.2fr_1fr_auto] gap-2">
                    <input data-testid={`${TESTIDS.ctpContactName}-${i}`} className="td-input !py-1.5 !text-xs" placeholder={t("ctp_contact_name")} value={c.name} onChange={(e) => updContact(i, "name", e.target.value)} />
                    <input data-testid={`${TESTIDS.ctpContactRole}-${i}`} className="td-input !py-1.5 !text-xs" placeholder={t("ctp_contact_role")} value={c.role || ""} onChange={(e) => updContact(i, "role", e.target.value)} />
                    <input data-testid={`${TESTIDS.ctpContactEmail}-${i}`} className="td-input !py-1.5 !text-xs" placeholder={t("ctp_contact_email")} value={c.email || ""} onChange={(e) => updContact(i, "email", e.target.value)} />
                    <input data-testid={`${TESTIDS.ctpContactPhone}-${i}`} className="td-input !py-1.5 !text-xs" placeholder={t("ctp_contact_phone")} value={c.phone || ""} onChange={(e) => updContact(i, "phone", e.target.value)} />
                    <button data-testid={`${TESTIDS.ctpContactRemove}-${i}`} onClick={() => rmContact(i)} className="text-[var(--td-text-3)] hover:text-red-400 px-1"><X size={13} /></button>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-5">
              <label className="td-label">{t("ctp_f_bank")}</label>
              <textarea data-testid={TESTIDS.ctpBank} rows={2} className="td-input resize-none" value={editing.bank_details || ""} onChange={(e) => upd("bank_details", e.target.value)} />
            </div>
            <div className="mb-6">
              <label className="td-label">{t("ctp_f_notes")}</label>
              <textarea data-testid={TESTIDS.ctpNotes} rows={2} className="td-input resize-none" value={editing.notes || ""} onChange={(e) => upd("notes", e.target.value)} />
            </div>

            <div className="flex items-center justify-end gap-2">
              <button data-testid={TESTIDS.ctpCancelBtn} onClick={cancel}
                      className="btn-ghost !py-2 !px-4 text-xs font-mono uppercase tracking-widest inline-flex items-center gap-1.5">
                <X size={12} /> {t("ctp_cancel_btn")}
              </button>
              <button data-testid={TESTIDS.ctpSaveBtn} onClick={save} disabled={saving}
                      className="btn-gold inline-flex items-center gap-2 disabled:opacity-50">
                <Save size={13} /> {saving ? t("cp_saving") : t("ctp_save_btn")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
